import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IApp } from '@core/models/app.interfaces';
import { buildQuerySelector } from '@core/utilities/utilities.constants';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectComponent } from '@shared/components/ng-select/ng-select.component';
import { NgSelectModule } from '@shared/components/ng-select/ng-select.module';
import { NgLabelTemplateDirective, NgMultiLabelTemplateDirective, NgOptgroupTemplateDirective, NgOptionTemplateDirective } from '@shared/components/ng-select/ng-templates.directive';
import { DisableWithValidationDirective } from '@shared/directives/disable-with-validation.directive';
import { WCAGLabelDirective } from '@shared/directives/wcag-label.directive';
import { cloneDeep } from 'lodash';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'formly-field-dictionary-select',
  template: `

    <fa-icon
          *ngIf="props.toolTip"
          pTooltip="{{props.toolTip.text | translate}}"
          tooltipStyleClass="tooltip-min-width"
          tooltipPosition="{{props.toolTip?.tooltipPosition ? props.toolTip?.tooltipPosition : 'top'}}"
          [icon]="props.icon?.iconProp ? props.toolTip.iconProp : ['fas', 'info-circle']">
    </fa-icon>

    <input type="text"
           *ngIf="isFreeText"
           [formControl]="formControl"
           [id]="elementId"
           class="form-control"
           [disable-with-validation]="disabledField"
           [disabled]="isFormDisabled"
           [formlyAttributes]="field"
           [placeholder]="''"
           [class.is-invalid]="showError">

    <input type="text"
           *ngIf="!isFreeText && !props.dummyLabel"
           [id]="elementId"
           tabindex="-1"
           class="wcag-visuallyhidden"
           readonly/>

    <ng-select
        wcag-label
        *ngIf="!isFreeText"
        [id]="elementId + '_dropdown'"
        [items]="items"
        [placeholder]="placeholder | translate"
        [title]="selectedTitle"
        [inputAttrs]="inputAttrs"
        [formControl]="formControl"
        [bindLabel]="props.bindLabel || 'name'"
        [bindValue]="props.bindValue || 'id'"
        [clearable]="clearable"
        [groupBy]="getGroupBy()"
        [selectableGroup]="true"
        [class.custom-disabled]="isDisabled"
        [disabled]="isFormDisabled"
        [dropdownPosition]="dropdownPosition"
        [searchable]="props.searchable"
        [loading]="isLoading"
        [tabIndex]="tabIndex"
        [notFoundText]="notFoundText | translate"
        [loadingText]="loadingText | translate"
        [appendTo]="appendTo"
        (change)="props.change && props.change(field, $event)"
        [multiple]="props.multiple"
        [virtualScroll]="virtualScroll"
        [closeOnSelect]="!props.multiple"
        (focus)="onFocus($event)"
        (clear)="onManualClear()">

        <ng-template ng-option-tmp let-item="item" let-index="index" let-search="searchTerm">
          <span *ngIf="item" [innerHTML]="item && item[props.bindLabel || 'name']"></span>
        </ng-template>

        <ng-template ng-label-tmp let-item="item" let-index="index">
          <span *ngIf="item" [innerHTML]="item && item[props.bindLabel || 'name']"></span>
        </ng-template>

        <ng-template ng-optgroup-tmp let-item="item">
          <span class="ng-option-label" [innerHTML]="item && item[props.bindLabel || 'name']"></span>
        </ng-template>

        <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
          <ng-container *ngIf="items && items.length > 0">
            <div class="ng-value" *ngFor="let item of items">
              <span class="ng-value-label" [innerHTML]="item && item[props.bindLabel || 'name']"></span>
              <span *ngIf="!disabledField" class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
            </div>
          </ng-container>
        </ng-template>


</ng-select>


  `,
  imports: [CommonModule, ReactiveFormsModule, FormlyModule, NgSelectModule, TranslateModule,
     FontAwesomeModule, TooltipModule, FormsModule, WCAGLabelDirective, 
     DisableWithValidationDirective, NgOptionTemplateDirective, NgOptgroupTemplateDirective,
    NgLabelTemplateDirective, NgMultiLabelTemplateDirective]
})
export class FormlyFieldDictionarySelectComponent extends FieldType implements OnInit, OnDestroy {
  onDestroy$ = new Subject<void>();
  isLoading = false;
  allItems = [];
  items = [];
  userManuallyClearedFlag = false;
  source: Observable<IApp.IDict[]>;
  fluxSource: Observable<IApp.IFluxDict>;
  dataProvided: boolean;
  clearable: boolean;
  virtualScroll = true;
  public notFoundText: string;
  public placeholder: string;
  public loadingText = 'COMMON.actionsLabel.loading';

  public translatePrefix;
  public appendTo = 'body';

  public sortItems = true;
  public inputAttrs = {};
  public selectedValue;
  public selectedTitle: string = '';

  disabledField = false;
  localFormDisabled = false;
  multiple: boolean;
  dropdownPosition = 'auto';

  public isFreeText = false;

  private unchangedData: any[];

  @ViewChild(NgSelectComponent, {static: false}) ngSelectComponent: NgSelectComponent;

  constructor(
    private elRef: ElementRef,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    if (this.props.appendTo === 'self') {
      this.props.appendTo = `#${ this.elementId }_dropdown`;
      this.dropdownPosition = 'bottom';
    }
    if (this.props.searchable === undefined) {
      this.props.searchable = true;
    }
    this.formControl.valueChanges
      .pipe(
        filter(val => val === null),
        takeUntil(this.onDestroy$),
      ).subscribe(val => {
        if (this.field.defaultValue) {
          this.formControl.setValue(this.field.defaultValue);
        }
      });

    this.appendTo = this.props.appendTo || 'body';
    this.getScrollableParent();
    this.localFormDisabled = this.props.formDisabled || this.props.disabled;
    if (this.form.enabled) {
      const formControlDisableFn = this.formControl.disable;
      const formControlEnableFn = this.formControl.enable;
      this.formControl.disable = opts => {
        if (this.props.ignoreLocalFormDisabled) {
          formControlDisableFn.apply(this.formControl, arguments);
        }
        this.disabledField = true;
      };

      this.formControl.enable = opts => {
        if (this.props.ignoreLocalFormDisabled) {
          formControlEnableFn.apply(this.formControl, arguments);
        }
        this.disabledField = false;
      };
    }

    if (this.props.virtualScroll !== undefined) {
      this.virtualScroll = this.props.virtualScroll;
    }

    if (this.props.sort === false) {
      this.sortItems = this.props.sort;
    }

    if (this.props.reload$) {
      this.props.reload$
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => this.reloadData());
    }

    this.translatePrefix = this.props.translatePrefix ? `${ this.props.translatePrefix }.` : '';
    this.source = this.props.source;
    this.fluxSource = this.props.fluxSource;
    this.dataProvided = this.props.dataProvided;
    this.clearable = 'clearable' in this.props ? this.props.clearable : true;
    if (this.fluxSource) {
      this.loadFluxData();
    } else if (this.dataProvided) {
      this.loadProvidedData();
    } else {
      this.loadData();
    }
    this.notFoundText = this.translatePrefix ? `${ this.translatePrefix }notFoundText` :
        `ADMIN_MODULE.termsAndConditions.notFoundText`;
    const placeholderText = !this.props.ignorePlaceholderTranslatePrefix ? `${ this.translatePrefix }${ this.props.placeholder }` : this.props.placeholder;
    this.props.placeholder && (this.placeholder = placeholderText);
    this.props.loadingText && (this.loadingText = this.props.loadingText);

    if (!this.props.multiple && !this.multiple) {
      this.formControl.valueChanges
        .pipe(
          takeUntil(this.onDestroy$),
          filter(() => !this.isFreeText),
        )
        .subscribe(val => {
          this.clearUnlisted();
        });
    }

    if (this.props.filter) {
      this.form.get(this.props.filter.field).valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(val => {
          this.checkIsFreeText();
          this.filterItems();
        });
      setTimeout(() => {
        this.checkIsFreeText();
        this.filterItems();
      });
    }

    this.setInputAttrs();
    this.resetDataOnLanguageChange();
    this.updateSelectedTitle();
    this.formControl.valueChanges.subscribe(() => {
      this.updateSelectedTitle();
    });
  }

updateSelectedTitle() {
  const value = this.formControl?.value;

  if (!value) {
      this.selectedTitle = this.placeholder ? this.translateService.instant(this.placeholder) : '';
    return;
  }
  //for multiple select
  if (Array.isArray(value)) {
    this.selectedTitle = value
      .map(v => this.getItemLabel(v))
      .join(', ');
  } else {
    // for single select
    this.selectedTitle = this.getItemLabel(value);
  }
}

getItemLabel(value: any): string {
  const labelKey = this.props.bindLabel || 'name';
  const found = this.items?.find(item => item[this.props.bindValue || 'id'] === value);
  return found ? this.htmlToText(found[labelKey]) : '';
}

htmlToText(value: unknown): string {
  if (typeof value !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = value;
  return div.textContent || '';
}

  private reloadData() {
    if (this.fluxSource) {
      this.loadFluxData();
    } else if (this.props.dataProvided) {
      this.loadProvidedData();
    } else {
      this.loadData();
    }
  }

  filterItems(): void {
    // Reset manual clear flag when items are filtered to allow auto-selection in new context
    this.userManuallyClearedFlag = false;
    if (this.props.filter) {
      const val = this.form.get(this.props.filter.field) && this.form.get(this.props.filter.field).value;
      if (val) {
        this.items = [...this.filterFn(this.props.filter.prop, val)];
        this.clearUnlisted();
      }
    } else {
      this.prePopulate();
    }
  }

  filterFn(prop, val) {
    let items = this.allItems.filter(i => i[prop] === val);
    if (items.length === 0) { items = this.allItems.filter(i => i[prop] === null); }
    return items;
  }

  get disabled() {
    return this.props.disabled;
  }

  get elementId(): string {
    if (this.field.id && this.field.id.includes('formly_')) {
      let prefix = this.form['id'];

      // check if in repeat section
      if (!prefix && this.field.parent.parent && this.field.parent.parent.parent && this.field.parent.parent.parent.type === 'repeat') {
        const cnt = this.field.parent.parent.key;
        prefix = `${this.field.parent.parent.parent['id']}_${cnt}`;
      }
      return `${prefix ? prefix + '_' : ''}${(this.field.key as string).replace('.', '_')}`;
    } else {
      return this.field.id;
    }
  }

  isFormDisabled(): boolean {
    return this.form.disabled === true || this.localFormDisabled;
  }

  private loadData() {
    this.isLoading = true;
    this.source && this.source
      .pipe(
        takeUntil(this.onDestroy$),
        filter(d => d !== null),
        map((data: IApp.IDict[]) => data.filter(el => this.props.skipItems ? !this.props.skipItems.includes(el.id) : true)),
        map(data => {
          if (!this.props.mapMissingFn || typeof this.props.mapMissingFn !== 'function') {
            return data;
          }
          const val = this.form.get(this.key as string) && this.form.get(this.key as string).value;
          return (!data.find(i => i.id === val)) ? [...data, this.props.mapMissingFn(val)] : data;
        }),
        tap(data => {
          if (this.props.onDataLoad && typeof this.props.onDataLoad === 'function') {
            this.props.onDataLoad(data);
          }
        }),
        tap(data => {
          this.unchangedData = data;
        }),
        map((data: IApp.IDict[]) => {
          return this.translateData(data);
        }),
      )
      .subscribe(data => {
        this.setData(data);
      },
        err => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        });
  }

  private loadFluxData() {
    this.isLoading = true;
    this.fluxSource
      .pipe(
        takeUntil(this.onDestroy$),
        tap(data => {
          this.unchangedData = data.roleDtos;
        }),
        map((data: IApp.IFluxDict) => {
         return this.translateData(this.unchangedData);
        }),
      )
      .subscribe(data => {
        this.allItems = data;
        this.items = data;
        if (this.sortItems) {
        this.items.sort((a, b) => a[this.props.bindLabel || 'name'].localeCompare(b[this.props.bindLabel || 'name']));
      } this.filterItems();
        this.filterItems();
        this.isLoading = false;
        this.cd.detectChanges();
    });
  }

  private loadProvidedData() {
    if (this.props.translateOptions && this.props.translatePrefix) {
      let data = cloneDeep(this.props.items);
      data = data.map(el => ({...el, name: this.translateService.instant(`${this.translatePrefix}${el[this.props.bindLabel || 'name']}`) }));
      this.allItems = data;
      this.items = data;
    } else {
      this.allItems = this.props.items;
      this.items = this.props.items;
    }
    this.cd.detectChanges();
  }

  get label() {
    let label = '';
    if (this.props.required) {
      label = label + 'Required field, ';
    }
    label += this.props.placeholder === 'selectItem' ? this.props.label : this.placeholder;
    return label;
  }

  prePopulate() {
    setTimeout(() => {
      const preventAutoPopulate = (typeof this.props.preventAutoPopulate === 'function') ? this.props.preventAutoPopulate() : this.props.preventAutoPopulate;
      if (!preventAutoPopulate && !this.userManuallyClearedFlag && this.ngSelectComponent && this.ngSelectComponent.itemsList.items.length === 1 && this.field.key !== 'opaAgreementId') {
        this.ngSelectComponent.select(this.ngSelectComponent.itemsList.items[0]);
      }
      if (!this.props.skipInitialBlur) {
        this.ngSelectComponent && this.ngSelectComponent.blur();
      }
    });
  }

  get isDisabled() {
    return this.disabledField;
  }

  getScrollableParent() {
    if (this.props.ignoreDropdownAppend || this.isFreeText || !this.ngSelectComponent) {
      return;
    }

    setTimeout(() => {
      let a = this.ngSelectComponent.element;
      const els = [];
      while (a) {
        els.unshift(a);
        a = a.parentNode as HTMLElement;
      }
      const revEls = els.reverse();
      const scrollableElIdx = revEls.findIndex(e => e.scrollHeight !== undefined && e.clientHeight !== undefined && e.clientHeight > 0 && e.scrollHeight > e.clientHeight + 1);
      if (scrollableElIdx >= 1) {
        const el = revEls[scrollableElIdx - 1];
        if (el.tagName !== 'APP-BASE-STEPPER') {
          el.style.position = 'relative';
          el.style.display = el.style.display || 'block';
          const _el = buildQuerySelector(el);
          this.appendTo = _el || 'body';
        }
      }
    });
  }

  clearUnlisted() {
    setTimeout(() => {
      if (!this.isFreeText) {
        const val = this.field.model && this.field.model[this.field.key as string];
        const tmp = this.items as any;
        const ids = tmp.map(i => ([i[this.props.bindValue || 'id'], ...this.props.groupBy && i[this.props.groupBy] ? i[this.props.groupBy].map(j => j[this.props.bindValue || 'id']) : []])).flat();
        if (val && this.items && this.items.length > 0 && !ids.includes(val)) {
          this.ngSelectComponent.handleClearClick();
        }
        this.prePopulate();
      }
    });
  }

  setInputAttrs() {
    const attrs = { 'aria-label': this.label };
    this.inputAttrs = attrs;
  }

  public get tabIndex(): number {
    return this.isDisabled || this.field.hide ? -1 : 0;
  }

  onFocus(event) {
    this.isDisabled && event && event.preventDefault();
  }

  getGroupBy(): string {
    if (this.props.groupBy && typeof this.props.groupBy === 'function') {
      return this.props.groupBy();
    }

    return this.props.groupBy;
  }

  private translateData(data) {
    if (this.props.sortByLang) {
      const d = cloneDeep(data);
      const langIndex = d.findIndex(el => el.lang === this.translateService.currentLang.toUpperCase());
      const firstItem = langIndex > -1 ? d.splice(langIndex, 1) : [];
      data = [...firstItem, ...d];
    }
    if (this.props.translateOptions && data) {
      const translated = data.map(el => ({
        ...el,
        name: this.translateService.instant(`${this.translatePrefix}${el[this.props.bindLabel || 'name']}`),
      }));
      if (this.sortItems) {
        return translated.sort((a, b) => a.name.localeCompare(b.name));
      } else if (this.field.key === 'country') {
        const topItems = translated.splice(0, 2);
        return [...topItems, ...translated.sort((a, b) => a.name.localeCompare(b.name))];
      } else {
        return translated;
      }
    } else {
      return data;
    }
  }

  private resetDataOnLanguageChange() {
    this.translateService.onLangChange
    .pipe(takeUntil(this.onDestroy$))
    .subscribe(() => {
      if (this.dataProvided) {
        this.loadProvidedData();
      } else {
        if (this.props.reloadSourceOnLangChange) {
          this.loadData();
        } else {
          const data = this.translateData(this.unchangedData);
          data && this.setData(data);
        }
      }
    });
  }

  private setData(data) {
    this.allItems = data;
    this.items = data;
    // Reset manual clear flag when new data is loaded to allow auto-selection
    this.userManuallyClearedFlag = false;
    if (this.field.key === 'accountId') {
          this.props.change && this.props.change(this.field, { id: this.model[this.field.key],
             certificateNumber: this.model['certificateNumber'],
            initial: true });
        } else {
          setTimeout(() => {
            this.model && this.props.change && this.props.change(this.field,
              {
                id: this.model[this.field.key as string],
                selectedItems: this.ngSelectComponent.selectedValues,
                initial: true,
              });
          });
        }
    this.filterItems();
    // if (!this.items.length && !this.isDisabled && !this.props.validateEmpty) {
    //   this.ngSelectComponent && this.ngSelectComponent.handleClearClick();
    //   this.formControl.setErrors(null);
    // }
    this.updateSelectedTitle();
    this.isLoading = false;
    this.cd.detectChanges();
  }

  checkIsFreeText(): void {
    this.isFreeText = this.props.freeTextExpr ? Function( `return ${this.props.freeTextExpr}`).bind(this)() : false;
  }

  onManualClear() {
    this.userManuallyClearedFlag = true;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
